import React, { useState, useRef } from 'react';
import { Plus, Waves, Edit, Trash2, List, MapPin, Globe, LayoutGrid, Anchor } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';

const POOL_TYPES = ["25m", "50m", "Open Water", "Ocean", "Quarry", "Lake", "River", "Indoor Pool", "Outdoor Pool", "Other"];
const ADDRESS_TYPES = ["Street", "Avenue", "Boulevard", "Road", "Drive", "Lane", "Place", "Way", "Court"];

const AquaticInfrastructureTab = ({ 
  aquaticInfrastructures, 
  onCreateInfrastructure, 
  onUpdateInfrastructure, 
  onDeleteInfrastructure 
}) => {
  const [viewMode, setViewMode] = useState('list'); // Default to list mode
  const [showModal, setShowModal] = useState(false);
  const [editingInfrastructure, setEditingInfrastructure] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  
  const [form, setForm] = useState({
    short_name: '',
    long_name: '',
    pool_type: '25m',
    pool_length: '',
    min_depth: '',
    max_depth: '',
    number_of_lanes: '',
    max_capacity: '',
    logo_url: '',
    website_url: '',
    address_number: '',
    address_street: '',
    address_type: 'Street',
    city: '',
    postal_code: '',
    province: '',
    country: '',
    organization: '',
    building_name: ''
  });

  const resetForm = () => {
    setForm({
      short_name: '',
      long_name: '',
      pool_type: '25m',
      pool_length: '',
      min_depth: '',
      max_depth: '',
      number_of_lanes: '',
      max_capacity: '',
      logo_url: '',
      website_url: '',
      address_number: '',
      address_street: '',
      address_type: 'Street',
      city: '',
      postal_code: '',
      province: '',
      country: '',
      organization: '',
      building_name: ''
    });
    setLogoPreview(null);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo file size must be less than 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setForm({...form, logo_url: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const clearLogo = () => {
    setLogoPreview(null);
    setForm({...form, logo_url: ''});
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const openNewModal = () => {
    setEditingInfrastructure(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (infrastructure) => {
    setEditingInfrastructure(infrastructure);
    setForm({
      short_name: infrastructure.short_name || '',
      long_name: infrastructure.long_name || '',
      pool_type: infrastructure.pool_type || '25m',
      pool_length: infrastructure.pool_length?.toString() || '',
      min_depth: infrastructure.min_depth?.toString() || '',
      max_depth: infrastructure.max_depth?.toString() || '',
      number_of_lanes: infrastructure.number_of_lanes?.toString() || '',
      max_capacity: infrastructure.max_capacity?.toString() || '',
      logo_url: infrastructure.logo_url || '',
      website_url: infrastructure.website_url || '',
      address_number: infrastructure.address_number || '',
      address_street: infrastructure.address_street || '',
      address_type: infrastructure.address_type || 'Street',
      city: infrastructure.city || '',
      postal_code: infrastructure.postal_code || '',
      province: infrastructure.province || '',
      country: infrastructure.country || '',
      organization: infrastructure.organization || '',
      building_name: infrastructure.building_name || ''
    });
    setLogoPreview(infrastructure.logo_url || null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const infrastructureData = {
      ...form,
      pool_length: parseFloat(form.pool_length) || 0,
      min_depth: parseFloat(form.min_depth) || 0,
      max_depth: parseFloat(form.max_depth) || 0,
      number_of_lanes: parseInt(form.number_of_lanes) || 0,
      max_capacity: parseInt(form.max_capacity) || 0
    };

    if (editingInfrastructure) {
      await onUpdateInfrastructure(editingInfrastructure.id, infrastructureData);
    } else {
      await onCreateInfrastructure(infrastructureData);
    }
    setShowModal(false);
    setEditingInfrastructure(null);
    resetForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this aquatic infrastructure?')) {
      return;
    }
    await onDeleteInfrastructure(id);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200">Aquatic Infrastructure</h2>
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex bg-blue-800/50 rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-teal-600' : 'text-blue-300 hover:text-white'}
              data-testid="infrastructure-grid-view-btn"
            >
              <LayoutGrid size={18} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-teal-600' : 'text-blue-300 hover:text-white'}
              data-testid="infrastructure-list-view-btn"
            >
              <List size={18} />
            </Button>
          </div>
          <Button 
            onClick={openNewModal}
            className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
            data-testid="add-pool-location-btn"
          >
            <Plus className="mr-2" size={18} />
            Add Pool Location
          </Button>
        </div>
      </div>

      {/* Infrastructure Cards Grid/List */}
      {aquaticInfrastructures.length === 0 ? (
        <Card className="bg-gradient-to-r from-blue-900/30 to-teal-900/30 border-blue-700/30">
          <CardContent className="p-8 text-center">
            <Anchor className="mx-auto mb-4 text-blue-400" size={48} />
            <h3 className="text-xl font-semibold text-blue-200 mb-2">No Pool Locations Yet</h3>
            <p className="text-blue-300 mb-4">Add your first aquatic infrastructure to start managing pool locations.</p>
            <Button 
              onClick={openNewModal}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Plus className="mr-2" size={16} />
              Add Pool Location
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {aquaticInfrastructures.map((infra) => (
            <Card key={infra.id} className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 hover:border-teal-500/50 transition-all" data-testid={`infrastructure-card-${infra.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    {infra.logo_url ? (
                      <img 
                        src={infra.logo_url} 
                        alt={infra.short_name}
                        className="w-12 h-12 rounded-lg object-cover border border-blue-500/50"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-blue-700/50 flex items-center justify-center border border-blue-500/50">
                        <Waves className="text-blue-300" size={24} />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-blue-200 text-lg">{infra.short_name}</CardTitle>
                      <p className="text-blue-400 text-sm">{infra.long_name}</p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      onClick={() => openEditModal(infra)}
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-blue-300 hover:bg-blue-700/50"
                      data-testid={`edit-infrastructure-${infra.id}`}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      onClick={() => handleDelete(infra.id)}
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-300 hover:bg-red-700/50"
                      data-testid={`delete-infrastructure-${infra.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Pool Specs */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-blue-900/30 rounded p-2">
                    <span className="text-blue-400 text-xs">Pool Type</span>
                    <p className="text-white font-medium">{infra.pool_type}</p>
                  </div>
                  <div className="bg-blue-900/30 rounded p-2">
                    <span className="text-blue-400 text-xs">Length</span>
                    <p className="text-white font-medium">{infra.pool_length}m</p>
                  </div>
                  <div className="bg-blue-900/30 rounded p-2">
                    <span className="text-blue-400 text-xs">Depth</span>
                    <p className="text-white font-medium">{infra.min_depth}m - {infra.max_depth}m</p>
                  </div>
                  <div className="bg-blue-900/30 rounded p-2">
                    <span className="text-blue-400 text-xs">Lanes</span>
                    <p className="text-white font-medium">{infra.number_of_lanes}</p>
                  </div>
                </div>
                
                {/* Capacity Badge */}
                <div className="flex items-center justify-between">
                  <Badge className="bg-teal-700/50 text-teal-200">
                    Max Capacity: {infra.max_capacity}
                  </Badge>
                </div>

                {/* Address */}
                <div className="text-sm text-blue-300 border-t border-blue-700/30 pt-3">
                  <p className="flex items-center">
                    <MapPin size={14} className="mr-1 text-teal-400" />
                    {infra.address_number && `${infra.address_number} `}
                    {infra.address_street} {infra.address_type}
                  </p>
                  <p className="ml-5">{infra.city}, {infra.postal_code}</p>
                  <p className="ml-5">{infra.province && `${infra.province}, `}{infra.country}</p>
                </div>
                
                {/* Website Link */}
                {infra.website_url && (
                  <a 
                    href={infra.website_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-teal-400 hover:text-teal-300 flex items-center"
                  >
                    <Globe size={14} className="mr-1" />
                    Visit Website
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-3">
          {aquaticInfrastructures.map((infra) => (
            <Card key={infra.id} className="bg-gradient-to-r from-blue-800/30 to-teal-800/30 border-blue-700/30 hover:border-teal-500/50 transition-all" data-testid={`infrastructure-list-item-${infra.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {infra.logo_url ? (
                      <img 
                        src={infra.logo_url} 
                        alt={infra.short_name}
                        className="w-14 h-14 rounded-lg object-cover border border-blue-500/50"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-blue-700/50 flex items-center justify-center border border-blue-500/50">
                        <Waves className="text-blue-300" size={24} />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-white font-semibold text-lg">{infra.short_name}</h3>
                      <p className="text-blue-400 text-sm">{infra.long_name}</p>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-blue-300">
                        <span>{infra.pool_type} • {infra.pool_length}m</span>
                        <span>Depth: {infra.min_depth}m - {infra.max_depth}m</span>
                        <span>{infra.number_of_lanes} lanes</span>
                        <Badge className="bg-teal-700/50 text-teal-200 text-xs">
                          Capacity: {infra.max_capacity}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right text-sm text-blue-300">
                      <p>{infra.address_number && `${infra.address_number} `}{infra.address_street} {infra.address_type}</p>
                      <p>{infra.city}, {infra.postal_code}</p>
                      {infra.website_url && (
                        <a 
                          href={infra.website_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-teal-400 hover:text-teal-300 flex items-center justify-end"
                        >
                          <Globe size={12} className="mr-1" />
                          Website
                        </a>
                      )}
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        onClick={() => openEditModal(infra)}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-blue-300 hover:bg-blue-700/50"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        onClick={() => handleDelete(infra.id)}
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-300 hover:bg-red-700/50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Infrastructure Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-blue-200">
              {editingInfrastructure ? 'Edit Pool Location' : 'Add New Pool Location'}
            </DialogTitle>
            <DialogDescription className="text-blue-300">
              {editingInfrastructure ? 'Update the pool location details.' : 'Add a new aquatic infrastructure to your system.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Names Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-200">Short Name *</Label>
                <Input
                  value={form.short_name}
                  onChange={(e) => setForm({...form, short_name: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder="e.g., Olympic Pool"
                  required
                  data-testid="infrastructure-short-name"
                />
              </div>
              <div>
                <Label className="text-blue-200">Long Name *</Label>
                <Input
                  value={form.long_name}
                  onChange={(e) => setForm({...form, long_name: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder="e.g., City Olympic Aquatic Center"
                  required
                  data-testid="infrastructure-long-name"
                />
              </div>
            </div>

            {/* Organization & Building */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-200">Organization</Label>
                <Input
                  value={form.organization}
                  onChange={(e) => setForm({...form, organization: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder="e.g., City Sports Authority"
                />
              </div>
              <div>
                <Label className="text-blue-200">Building Name</Label>
                <Input
                  value={form.building_name}
                  onChange={(e) => setForm({...form, building_name: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white"
                  placeholder="e.g., Sports Complex Building A"
                />
              </div>
            </div>

            {/* Pool Specifications */}
            <div className="border border-blue-700/50 rounded-lg p-4">
              <h4 className="text-teal-200 font-semibold mb-3">Pool Specifications</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-blue-200">Pool Type *</Label>
                  <Select 
                    value={form.pool_type} 
                    onValueChange={(value) => setForm({...form, pool_type: value})}
                  >
                    <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-blue-800 border-blue-600">
                      {POOL_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-blue-200">Pool Length (m) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.pool_length}
                    onChange={(e) => setForm({...form, pool_length: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="25"
                    required
                  />
                </div>
                <div>
                  <Label className="text-blue-200">Number of Lanes *</Label>
                  <Input
                    type="number"
                    value={form.number_of_lanes}
                    onChange={(e) => setForm({...form, number_of_lanes: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="8"
                    required
                  />
                </div>
                <div>
                  <Label className="text-blue-200">Min Depth (m) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.min_depth}
                    onChange={(e) => setForm({...form, min_depth: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="1.2"
                    required
                  />
                </div>
                <div>
                  <Label className="text-blue-200">Max Depth (m) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.max_depth}
                    onChange={(e) => setForm({...form, max_depth: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="3.5"
                    required
                  />
                </div>
                <div>
                  <Label className="text-blue-200">Max Capacity *</Label>
                  <Input
                    type="number"
                    value={form.max_capacity}
                    onChange={(e) => setForm({...form, max_capacity: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="50"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="border border-blue-700/50 rounded-lg p-4">
              <h4 className="text-teal-200 font-semibold mb-3">Address</h4>
              <div className="grid grid-cols-4 gap-4 mb-3">
                <div>
                  <Label className="text-blue-200">Number</Label>
                  <Input
                    value={form.address_number}
                    onChange={(e) => setForm({...form, address_number: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="123"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-blue-200">Street *</Label>
                  <Input
                    value={form.address_street}
                    onChange={(e) => setForm({...form, address_street: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="Main"
                    required
                  />
                </div>
                <div>
                  <Label className="text-blue-200">Type</Label>
                  <Select 
                    value={form.address_type} 
                    onValueChange={(value) => setForm({...form, address_type: value})}
                  >
                    <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-blue-800 border-blue-600">
                      {ADDRESS_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label className="text-blue-200">City *</Label>
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({...form, city: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="Montreal"
                    required
                  />
                </div>
                <div>
                  <Label className="text-blue-200">Postal Code *</Label>
                  <Input
                    value={form.postal_code}
                    onChange={(e) => setForm({...form, postal_code: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="H2X 1Y4"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-blue-200">Province/State</Label>
                  <Input
                    value={form.province}
                    onChange={(e) => setForm({...form, province: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="Quebec"
                  />
                </div>
                <div>
                  <Label className="text-blue-200">Country *</Label>
                  <Input
                    value={form.country}
                    onChange={(e) => setForm({...form, country: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="Canada"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Logo Upload */}
            <div>
              <Label className="text-blue-200">Logo</Label>
              <div className="flex items-center gap-4">
                {(logoPreview || form.logo_url) && (
                  <div className="relative">
                    <img 
                      src={logoPreview || form.logo_url} 
                      alt="Pool logo" 
                      className="w-16 h-16 rounded-lg object-cover border-2 border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={clearLogo}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    ref={logoInputRef}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    className="bg-blue-800/50 border-blue-600 text-blue-200 hover:bg-blue-700/50"
                  >
                    {(logoPreview || form.logo_url) ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  <p className="text-xs text-blue-300 mt-1">Max 5MB, JPG/PNG</p>
                </div>
              </div>
            </div>

            {/* Website URL */}
            <div>
              <Label className="text-blue-200">Website URL</Label>
              <Input
                type="url"
                value={form.website_url}
                onChange={(e) => setForm({...form, website_url: e.target.value})}
                className="bg-blue-800/50 border-blue-600 text-white"
                placeholder="https://www.example.com"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-blue-500 text-blue-200">
                Cancel
              </Button>
              <Button type="submit" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700" data-testid="save-infrastructure-btn">
                {editingInfrastructure ? 'Update' : 'Create'} Pool Location
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AquaticInfrastructureTab;
